import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronRight, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useNavigate } from 'react-router-dom';

export function OnboardingProgressBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isLoading,
    isComplete,
    isOnboardingRoute,
    currentStep,
    totalCount,
    completedCount,
    progressPercent,
    isCurrentStepCompleted,
    nextStep,
    goToNextStep,
  } = useOnboarding();

  const [showCompletedAnimation, setShowCompletedAnimation] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  // Detect when current step gets completed
  useEffect(() => {
    if (isCurrentStepCompleted && !justCompleted) {
      setJustCompleted(true);
      setShowCompletedAnimation(true);
      
      const timer = setTimeout(() => {
        setShowCompletedAnimation(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isCurrentStepCompleted, justCompleted]);

  // Reset justCompleted when step changes
  useEffect(() => {
    setJustCompleted(false);
  }, [currentStep?.step_key]);

  // Don't show if loading, complete, or not on an onboarding route
  if (isLoading || isComplete || !isOnboardingRoute || !currentStep) {
    return null;
  }

  const stepTitle = t(currentStep.title_key, currentStep.step_key);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -60, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={`
          relative z-20 px-4 py-2.5 
          bg-gradient-to-r from-[#3ECDC6] to-[#2B7CDB]
          border-b border-white/20
          shadow-lg
          ${showCompletedAnimation ? 'animate-pulse' : ''}
        `}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Left: Step info */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-white/80 flex-shrink-0" />
              <span className="text-white/90 text-sm font-medium whitespace-nowrap">
                {t('guide.progress', { 
                  completed: completedCount, 
                  total: totalCount 
                })}
              </span>
            </div>
            
            <span className="text-white font-semibold text-sm truncate">
              {stepTitle}
            </span>

            {/* Completed badge */}
            <AnimatePresence>
              {isCurrentStepCompleted && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full"
                >
                  <Check className="w-3.5 h-3.5 text-white" />
                  <span className="text-white text-xs font-medium">
                    {t('guide.progressBar.completed', 'Completado')}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Center: Progress bar */}
          <div className="hidden sm:flex items-center gap-3 flex-1 max-w-xs">
            <Progress 
              value={progressPercent} 
              className="h-2 bg-white/20 flex-1"
            />
            <span className="text-white/90 text-xs font-medium whitespace-nowrap">
              {progressPercent}%
            </span>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {isCurrentStepCompleted && nextStep ? (
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={goToNextStep}
                  className="bg-white text-[#3ECDC6] hover:bg-white/90 font-semibold shadow-md"
                >
                  {t('guide.progressBar.next', 'Siguiente')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            ) : isCurrentStepCompleted && !nextStep ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate('/getting-started')}
                className="bg-white text-[#3ECDC6] hover:bg-white/90 font-semibold shadow-md"
              >
                {t('guide.progressBar.allDone', 'Finalizar')}
                <Check className="w-4 h-4 ml-1" />
              </Button>
            ) : null}

            {/* Close/minimize button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => navigate('/getting-started')}
              className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10"
              title={t('common.back', 'Volver')}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile progress bar */}
        <div className="sm:hidden mt-2">
          <div className="flex items-center gap-2">
            <Progress 
              value={progressPercent} 
              className="h-1.5 bg-white/20 flex-1"
            />
            <span className="text-white/80 text-xs">
              {progressPercent}%
            </span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
